import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { CreateProgrammeResponse } from '../../../api/programme/v2/programme.types';
import { EditorEmailTemplate, RootState } from '../../../types/common.types';
import {
  EmailTemplate,
  EmailTemplateProgramme,
  EmailTemplateType,
  Member,
  Programme,
  Team,
  TeamMemberExcelFailure,
  TrackingDeviceAssignment
} from '../../../types/entity.types';

import { useDeviceAssignmentApi } from '../../../api/device-assignment/useDeviceAssignmentApi';
import { useEmailTemplateApi } from '../../../api/email-template/useEmailTemplateApi';
import { useMemberApi } from '../../../api/member/useMemberApi';
import { useProgrammeTeamsApi } from '../../../api/programme-teams/useProgrammeTeamsApi';
import { useProgrammeApi } from '../../../api/programme/useProgrammeApi';
import { useV2ProgrammeApi } from '../../../api/programme/v2/useV2ProgrammeApi';
import { useTeamMemberApi } from '../../../api/team-member/useTeamMemberApi';
import { useTeamApi } from '../../../api/team/useTeamApi';

import { setAccountabilityTemplate } from '../../../slices/programme/accountability/accountability.slice';
import { setMembers } from '../../../slices/programme/member/member.slice';
import { setCollections } from '../../../slices/programme/plan/plan.slice';
import { setParentProgramme, setProgramme, setSubProgramme } from '../../../slices/programme/programme.slice';
import { setSupportTemplate } from '../../../slices/programme/support/support.slice';
import { setTeam } from '../../../slices/programme/team/team.slice';
import { setTribeTemplate } from '../../../slices/programme/tribe/tribe.slice';

import { NEW, TEAMS, UPCOMING } from '../../../utils/constants';
import { PARENT_EMAIL_TARGET, mapEmailTemplatesToVOs } from '../../../utils/general.util';
import { downloadExcelFile, fileToBase64, isProgrammeIncomplete } from '../../../utils/util';

import General from '../../../components/screens/programme/general/General';

const GeneralContainer = () => {
  const dispatch = useDispatch();

  const navigate = useNavigate();

  const { status, programmeExternalReference, view, teamExternalReference } = useParams();

  const { programme, parentProgramme, subProgramme, programmeSummaries } = useSelector(
    ({ programme }: RootState) => programme
  );
  const { members } = useSelector(({ member }: RootState) => member);
  const { team, teams } = useSelector(({ team }: RootState) => team);

  const {
    viewProgramme,
    updateProgramme: updateProgrammeRequest,
    uploadMemberExcel: uploadMemberExcelRequest,
    activateProgramme: activateProgrammeRequest,
    sendMemberActivationCommunications: sendMemberActivationCommunicationsRequest
  } = useProgrammeApi();

  const {
    viewAllProgrammes,
    createProgramme: createProgrammeRequest,
    viewParentProgramme,
    viewSubProgramme,
    updateParentProgramme: updateParentProgrammeRequest,
    updateSubProgramme: updateSubProgrammeRequest,
    publishProgramme,
    archiveProgramme: archiveProgrammeRequest
  } = useV2ProgrammeApi();

  const {
    createMember: createMemberRequest,
    deleteMember: deleteMemberRequest,
    updateMember: updateMemberRequest,
    viewAllMembers,
    downloadMemberExcel: downloadMemberExcelRequest
  } = useMemberApi();

  const {
    createTeam: createTeamRequest,
    viewTeam,
    updateTeam: updateTeamRequest,
    deleteTeam: deleteTeamRequest
  } = useTeamApi();

  const { addMemberToTeam: addMemberToTeamRequest, removeMemberFromTeam: removeMemberFromTeamRequest } =
    useTeamMemberApi();

  const {
    viewTeamsForProgramme,
    downloadTeamExcel: downloadTeamExcelRequest,
    uploadTeamExcel: uploadTeamExcelRequest
  } = useProgrammeTeamsApi();

  const { viewEmailTemplates, saveEmailTemplates, testEmailTemplate } = useEmailTemplateApi();

  const {
    createTrackingDeviceAssignment: createTrackingDeviceAssignmentRequest,
    retrieveTrackingDeviceAssignments: retrieveTrackingDeviceAssignmentsRequest
  } = useDeviceAssignmentApi();

  const emailTemplateTargetExternalReference = subProgramme?.externalReference ?? programmeExternalReference;

  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (status === NEW) {
      dispatch(
        setProgramme({
          name: '',
          description: '',
          fromDate: undefined,
          toDate: undefined,
          theme: undefined,
          isNutrition: true,
          isMovement: true,
          isMeasurements: true,
          isSleep: true,
          isCondition: true,
          isSelfCare: true,
          isSession: true,
          status: UPCOMING
        })
      );
      dispatch(setParentProgramme(undefined));
      dispatch(setSubProgramme(undefined));
      dispatch(setMembers([]));
      dispatch(setCollections([]));
      dispatch(setAccountabilityTemplate(undefined));
      dispatch(setSupportTemplate(undefined));
      dispatch(setTribeTemplate(undefined));
    }
  }, [status, dispatch]);

  useEffect(() => {
    if (!programmeExternalReference) return;

    const hasSubProgrammes = searchParams.get('hasSubProgrammes') === 'true';
    if (!hasSubProgrammes) {
      viewProgramme({ externalReference: programmeExternalReference });

      viewAllMembers({ programmeExternalReference });

      dispatch(setParentProgramme(undefined));
      dispatch(setSubProgramme(undefined));
      return;
    }

    viewParentProgramme({ programmeExternalReference }, (data) => {
      if (!data?.subProgrammeSummaries || data.subProgrammeSummaries.length <= 0) return;

      const subProgrammeExternalReference = data.subProgrammeSummaries[0]?.externalReference;
      if (!subProgrammeExternalReference) return;

      viewSubProgramme({
        programmeExternalReference,
        subProgrammeExternalReference
      });

      viewAllMembers({ programmeExternalReference: subProgrammeExternalReference });

      dispatch(setProgramme(undefined));
    });
  }, [
    dispatch,
    programmeExternalReference,
    searchParams,
    viewParentProgramme,
    viewSubProgramme,
    viewProgramme,
    viewAllMembers
  ]);

  useEffect(() => {
    if ((!programmeExternalReference && !subProgramme) || view !== TEAMS) return;

    viewTeamsForProgramme({
      programmeExternalReference: (subProgramme ? subProgramme.externalReference : programmeExternalReference) as string
    });
  }, [dispatch, programmeExternalReference, view, subProgramme, viewTeamsForProgramme]);

  useEffect(() => {
    if (teamExternalReference) {
      viewTeam({ teamExternalReference });
    } else {
      dispatch(setTeam(undefined));
    }
  }, [dispatch, teamExternalReference, viewTeam]);

  const isProgrammeComplete = useMemo(() => {
    if (!programmeSummaries || programmeSummaries.length === 0 || status !== UPCOMING) return false;

    const currentProgramme = programmeSummaries.find((p) => p.externalReference === programmeExternalReference);

    if (!currentProgramme) return false;

    return !isProgrammeIncomplete(currentProgramme);
  }, [programmeSummaries, programmeExternalReference, status]);

  const createProgramme = (
    programme: Programme,
    emailTemplatesToCreate: Record<string, EditorEmailTemplate[]>,
    onSuccess: (data: CreateProgrammeResponse) => void
  ) => {
    createProgrammeRequest(
      {
        payload: {
          charityWorkEnabled: !!programme.charityWork,
          conditionEnabled: !!programme.isCondition,
          description: programme.description,
          leaderboardCollectFrom: programme.leaderboardCollectFrom,
          measurementEnabled: !!programme.isMeasurements,
          movementEnabled: !!programme.isMovement,
          name: programme.name,
          nutritionEnabled: !!programme.isNutrition,
          runningFromDate: programme.fromDate as Date,
          runningToDate: programme.toDate as Date,
          selfCareEnabled: !!programme.isSelfCare,
          sessionEnabled: !!programme.isSession,
          sleepEnabled: !!programme.isSleep,
          theme: programme.theme as string,
          hasRegistrationRequirements: !!programme.hasRegistrationRequirements,
          payfastMerchantId: programme.registrationRequirement?.payfastMerchantId,
          payfastMerchantKey: programme.registrationRequirement?.payfastMerchantKey,
          programmePrice: programme.registrationRequirement?.programmePrice,
          requiredRegistrationFields: programme.registrationRequirement?.requiredRegistrationFields as string[],
          subProgrammes: programme.subProgrammes?.map(({ name, price }) => ({ name, price }))
        }
      },
      (data) => {
        const { programmeExternalReference, subProgrammeReferences } = data.data;

        const emailTemplates = Object.entries(emailTemplatesToCreate).flatMap(([target, templates]) => {
          const targetExternalReference =
            target === PARENT_EMAIL_TARGET
              ? programmeExternalReference
              : subProgrammeReferences[programme.subProgrammes?.find(({ id }) => id === target)?.name ?? ''];

          if (!targetExternalReference) return [];

          return mapEmailTemplatesToVOs(templates, targetExternalReference);
        });

        if (emailTemplates.length > 0) {
          saveEmailTemplates({ emailTemplates });
        }

        viewAllProgrammes();
        onSuccess(data);
      }
    );
  };

  const updateProgramme = (
    programme: Programme,
    emailTemplates: Record<string, EditorEmailTemplate[]>,
    onSuccess: () => void,
    changedParts: string[] = []
  ) => {
    const saveTemplates = () => {
      if (!emailTemplateTargetExternalReference) return;

      const emailTemplateVOs = mapEmailTemplatesToVOs(
        Object.values(emailTemplates).flat(),
        emailTemplateTargetExternalReference
      );

      if (emailTemplateVOs.length > 0) {
        saveEmailTemplates({ emailTemplates: emailTemplateVOs });
      }
    };

    if (!parentProgramme || !subProgramme) {
      updateNormalProgramme(programme, () => {
        saveTemplates();
        viewAllProgrammes();
        onSuccess();
      });
      return;
    }

    const isParentUpdated = changedParts.includes('parent');
    const isSubUpdated = changedParts.includes('sub');

    if (isParentUpdated && isSubUpdated) {
      updateParentProgramme(programme, () => {
        updateSubProgramme(programme, () => {
          saveTemplates();
          viewAllProgrammes();
          onSuccess();
        });
      });
    } else if (isParentUpdated) {
      updateParentProgramme(programme, () => {
        saveTemplates();
        viewAllProgrammes();
        onSuccess();
      });
    } else if (isSubUpdated) {
      updateSubProgramme(programme, () => {
        saveTemplates();
        onSuccess();
      });
    }
  };

  const updateNormalProgramme = (programme: Programme, onSuccess: () => void) => {
    updateProgrammeRequest(
      {
        programme: {
          ...programme,
          externalReference: programmeExternalReference
        }
      },
      onSuccess
    );
  };

  const updateParentProgramme = (programme: Programme, onSuccess: () => void) => {
    updateParentProgrammeRequest(
      {
        programmeExternalReference: programmeExternalReference as string,
        payload: {
          payload: {
            name: programme.name as string,
            runningFromDate: programme.fromDate as Date,
            runningToDate: programme.toDate as Date
          }
        }
      },
      onSuccess
    );
  };

  const updateSubProgramme = (programme: Programme, onSuccess: () => void) => {
    updateSubProgrammeRequest(
      {
        programmeExternalReference: programmeExternalReference as string,
        subProgrammeExternalReference: subProgramme!.externalReference as string,
        payload: {
          payload: {
            charityWorkEnabled: !!programme.charityWork,
            conditionEnabled: !!programme.isCondition,
            description: programme.description,
            hasRegistrationRequirements: !!programme.hasRegistrationRequirements,
            leaderboardCollectFrom: programme.leaderboardCollectFrom as Date,
            measurementEnabled: !!programme.isMeasurements,
            movementEnabled: !!programme.isMovement,
            name: programme.subProgrammeName as string,
            nutritionEnabled: !!programme.isNutrition,
            payfastMerchantId: programme.registrationRequirement?.payfastMerchantId,
            payfastMerchantKey: programme.registrationRequirement?.payfastMerchantKey,
            programmePrice: programme.registrationRequirement?.programmePrice,
            requiredRegistrationFields: programme.registrationRequirement?.requiredRegistrationFields as string[],
            selfCareEnabled: !!programme.isSelfCare,
            sessionEnabled: !!programme.isSession,
            sleepEnabled: !!programme.isSleep,
            theme: programme.theme as string
          }
        }
      },
      onSuccess
    );
  };

  const createMember = (member: Member, onSuccess: () => void) => {
    const externalReference = subProgramme ? subProgramme.externalReference : programmeExternalReference;

    createMemberRequest(
      {
        programmeExternalReference: externalReference as string,
        member
      },
      onSuccess
    );
  };

  const updateMember = (member: Member, onSuccess?: () => void) => {
    const externalReference = subProgramme ? subProgramme.externalReference : programmeExternalReference;

    updateMemberRequest(
      {
        programmeExternalReference: externalReference as string,
        member
      },
      onSuccess
    );
  };

  const deleteMember = (memberExternalReference: string, onSuccess: () => void) => {
    const externalReference = subProgramme ? subProgramme.externalReference : programmeExternalReference;

    deleteMemberRequest(
      {
        programmeExternalReference: externalReference as string,
        memberExternalReference
      },
      onSuccess
    );
  };

  const downloadMemberExcel = () => {
    const externalReference = subProgramme ? subProgramme.externalReference : programmeExternalReference;
    const name = subProgramme ? subProgramme.name : programme?.name;

    downloadMemberExcelRequest(
      {
        programmeExternalReference: externalReference as string
      },
      (data) => downloadExcelFile(data?.data, `${name} - Members.xlsx`)
    );
  };

  const uploadMemberExcel = async (file: File) => {
    const externalReference = subProgramme ? subProgramme.externalReference : programmeExternalReference;

    const base64String = await fileToBase64(file);
    uploadMemberExcelRequest({
      programmeExternalReference: externalReference as string,
      data: base64String
    });
  };

  const activateProgramme = () => {
    const handleSuccess = () => {
      viewAllProgrammes();
      navigate(`/programmes/active/${programmeExternalReference}/general/members?hasSubProgrammes=${!!subProgramme}`);
    };

    if (subProgramme) {
      publishProgramme({ programmeExternalReference: programmeExternalReference as string }, handleSuccess);
      return;
    }

    activateProgrammeRequest(
      {
        externalReference: programmeExternalReference as string
      },
      handleSuccess
    );
  };

  const archiveProgramme = () => {
    archiveProgrammeRequest({ programmeExternalReference: programmeExternalReference as string }, () => {
      viewAllProgrammes();
      navigate(`/programmes/archived/${programmeExternalReference}/general/members?hasSubProgrammes=${!!subProgramme}`);
    });
  };

  const createTeam = (team: Team, onSuccess: () => void) => {
    const externalReference = subProgramme ? subProgramme.externalReference : programmeExternalReference;
    createTeamRequest(
      {
        memberExternalReferences: team.memberExternalReferences ?? [],
        programmeExternalReference: externalReference as string,
        teamName: team.teamName as string
      },
      onSuccess
    );
  };

  const updateTeam = (team: Team, onSuccess: () => void) => {
    updateTeamRequest(
      {
        teamExternalReference: teamExternalReference as string,
        teamName: team.teamName as string
      },
      onSuccess
    );
  };

  const deleteTeam = (onSuccess: () => void) => {
    deleteTeamRequest(
      {
        teamExternalReference: teamExternalReference as string
      },
      onSuccess
    );
  };

  const addMemberToTeam = (memberExternalReference: string, onSuccess: () => void) => {
    addMemberToTeamRequest(
      {
        teamExternalReference: teamExternalReference as string,
        memberExternalReference
      },
      onSuccess
    );
  };

  const removeMemberFromTeam = (memberExternalReference: string, onSuccess: () => void) => {
    removeMemberFromTeamRequest(
      {
        teamExternalReference: teamExternalReference as string,
        memberExternalReference
      },
      onSuccess
    );
  };

  const downloadTeamExcel = () => {
    const externalReference = subProgramme ? subProgramme.externalReference : programmeExternalReference;

    downloadTeamExcelRequest({
      programmeExternalReference: externalReference as string
    });
  };

  const uploadTeamExcel = async (file: File, onError: (value: TeamMemberExcelFailure[]) => void) => {
    const externalReference = subProgramme ? subProgramme.externalReference : programmeExternalReference;
    const base64 = await fileToBase64(file);

    uploadTeamExcelRequest(
      {
        programmeExternalReference: externalReference as string,
        data: base64
      },
      onError
    );
  };

  const sendMemberActivationCommunications = (
    memberExternalReference: string,
    sendSms: boolean,
    sendEmail: boolean
  ) => {
    const externalReference = subProgramme ? subProgramme.externalReference : programmeExternalReference;
    sendMemberActivationCommunicationsRequest({
      programmeExternalReference: externalReference as string,
      memberExternalReference,
      sendSms,
      sendEmail
    });
  };

  const retrieveMembers = (externalReference: string) => {
    viewAllMembers({ programmeExternalReference: externalReference });
  };

  const createTrackingDeviceAssignment = (
    systemUserExternalReference: string,
    deviceId: string,
    onSuccess?: (data: TrackingDeviceAssignment) => void
  ) => {
    createTrackingDeviceAssignmentRequest({ systemUserExternalReference, deviceId }, onSuccess);
  };

  const retrieveTrackingDeviceAssignments = (
    systemUserExternalReference: string,
    onSuccess?: (data: TrackingDeviceAssignment[]) => void
  ) => {
    retrieveTrackingDeviceAssignmentsRequest({ systemUserExternalReference }, onSuccess);
  };

  const retrieveEmailTemplateByType = (
    programmeExternalReference: string,
    type: EmailTemplateType,
    onSuccess: (data: EmailTemplate[]) => void
  ) => {
    viewEmailTemplates({ programmeExternalReference, type }, onSuccess);
  };

  const sendTestEmail = (template: EditorEmailTemplate, emailAddress: string, programme: EmailTemplateProgramme) => {
    const [emailTemplate] = mapEmailTemplatesToVOs([template], emailTemplateTargetExternalReference ?? 'NEW');
    if (!emailTemplate) return;

    testEmailTemplate({ emailAddress, programme, emailTemplate });
  };

  return (
    <General
      programme={programme}
      parentProgramme={parentProgramme}
      subProgramme={subProgramme}
      members={members}
      team={team}
      teams={teams}
      isProgrammeComplete={isProgrammeComplete}
      createProgramme={createProgramme}
      updateProgramme={updateProgramme}
      viewParentProgramme={viewParentProgramme}
      viewSubProgramme={viewSubProgramme}
      createMember={createMember}
      updateMember={updateMember}
      deleteMember={deleteMember}
      downloadMemberExcel={downloadMemberExcel}
      uploadMemberExcel={uploadMemberExcel}
      activateProgramme={activateProgramme}
      archiveProgramme={archiveProgramme}
      createTeam={createTeam}
      updateTeam={updateTeam}
      deleteTeam={deleteTeam}
      retrieveMembers={retrieveMembers}
      createTrackingDeviceAssignment={createTrackingDeviceAssignment}
      retrieveTrackingDeviceAssignments={retrieveTrackingDeviceAssignments}
      addMemberToTeam={addMemberToTeam}
      removeMemberFromTeam={removeMemberFromTeam}
      uploadTeamExcel={uploadTeamExcel}
      downloadTeamExcel={downloadTeamExcel}
      sendMemberActivationCommunications={sendMemberActivationCommunications}
      retrieveEmailTemplateByType={retrieveEmailTemplateByType}
      sendTestEmail={sendTestEmail}
    />
  );
};

export default GeneralContainer;
